import { DataTable } from '@/components/features/shared/DataTable'
import DateSelectorComponent from '@/components/features/shared/DateSelector'
import { LoaderElement } from '@/components/features/shared/LoaderElement'
import { NavHeader } from '@/components/features/shared/NavHeader'
import { PageContainer } from '@/components/features/shared/PageContainer'
import { Summary } from '@/components/features/shared/Summary'
import { getVoucherIcon } from '@/components/features/shared/utils'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatInvoiceStatus, formatInvoiceType, getInvoiceStatusColor, InvoiceStatus, VoucherType } from '@/domain/invoices/invoice'
import type { InvoiceDetail } from '@/domain/invoices/invoice-item'
import { useOpenDocument } from '@/hooks/api/useDocuments'
import { useDataInvoices, useExportInvoices } from '@/hooks/api/useInvoices'
import { Paths } from '@/routes/paths'
import { formatDocNumber } from '@/utils/helpers'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import { addDays, endOfDay, format, startOfDay } from 'date-fns'
import { es } from 'date-fns/locale'
import { ArrowRight, Clipboard, CreditCard, Download, Receipt, RefreshCw, Search, X } from 'lucide-react'
import { useEffect, useState } from 'react'

export const InvoicePage = () => {
  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false }) as any

  const search = searchParams.search || ''
  const statusFilter = searchParams.status || 'all'
  const voucherTypeFilter = searchParams.voucherType || 'all'
  const page = Number(searchParams.page) || 1
  const pageSize = Number(searchParams.pageSize) || 20

  const [inputValue, setInputValue] = useState(search)
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: page - 1, pageSize })
  const [fromDate, setFromDate] = useState<Date | undefined>(() => {
    if (searchParams.fromDate) {
      return new Date(searchParams.fromDate)
    }
    return startOfDay(new Date())
  })
  const [toDate, setToDate] = useState<Date | undefined>(() => {
    if (searchParams.toDate) {
      return new Date(searchParams.toDate)
    }
    return endOfDay(addDays(new Date(), 1))
  })

  useEffect(() => {
    setInputValue(search)
  }, [search])

  const updateQueryParams = (newParams: Record<string, any>) => {
    navigate({
      search: (old: any) => ({ ...old, ...newParams }),
      replace: true,
    } as any)
  }

  const invoiceFilters = {
    search: search || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    voucherType: voucherTypeFilter === 'all' ? undefined : voucherTypeFilter,
    fromDate: fromDate ? format(fromDate, 'yyyy-MM-dd') : undefined,
    toDate: toDate ? format(toDate, 'yyyy-MM-dd') : undefined,
  }

  const { data: invoices, isLoading: invoicesLoading } = useDataInvoices({
    ...invoiceFilters,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
  })

  const { mutate: openDocument } = useOpenDocument()
  const { mutate: exportInvoices, isPending: exporting } = useExportInvoices()

  const handleOpenDocument = (documentId: string, fileName: string) => {
    if (!documentId || !fileName) return
    openDocument({ documentId, fileName })
  }

  const handleClearFilters = () => {
    setInputValue('')
    updateQueryParams({ search: '', status: 'all', voucherType: 'all', fromDate: '', toDate: '', page: 1 })
    setPagination({ pageIndex: 1, pageSize: 10 })
    setFromDate(undefined)
    setToDate(undefined)
  }

  const handleSearch = () => {
    updateQueryParams({ search: inputValue, page: 1 })
  }

  const handleExportCsv = () => {
    exportInvoices(invoiceFilters)
  }

  if (invoicesLoading) {
    return <LoaderElement />
  }

  const columns: ColumnDef<InvoiceDetail>[] = [
    {
      accessorKey: 'invoiceNumber',
      header: 'Número',
      cell: ({ row }) => {
        const invoice = row.original
        return (
          <div className="flex flex-col">
            {Number(invoice.invoiceNumber) > 0 && <span className="font-medium">{invoice.pointOfSaleNumber}-{invoice.invoiceNumber}</span>}
            <span className="text-sm text-muted-foreground">
              {formatInvoiceType(invoice.voucherType)} {invoice.invoiceType}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'relatedDocumentNumber',
      header: 'Documento Relacionado',
      cell: ({ row }) => {
        const invoice = row.original
        return (
          <div className="flex flex-col w-[max-content]">
            {Number(invoice.relatedDocumentNumber) > 0 ?
              <span className="font-medium">{invoice.relatedDocumentNumber}</span> :
              '-'}
            {invoice.relatedDocumentType && <span className="text-sm text-muted-foreground">
              {formatInvoiceType(invoice.relatedDocumentType)} {invoice.invoiceType}
            </span>}
          </div>
        )
      },
    },
    {
      accessorKey: 'contractNumber',
      header: 'Contrato',
      cell: ({ row }) => {
        const invoice = row.original
        return (
          <div className="flex flex-col w-[max-content]">
            <span className="font-medium">{invoice.contractNumber}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'customerCode',
      header: 'Cliente',
      cell: ({ row }) => {
        const invoice = row.original
        return (
          <span className="text-sm">{`${invoice.customerCode}`}</span>
        )
      },
    },
    {
      accessorKey: 'customerName',
      header: 'Razón Social / Nombre legal',
      cell: ({ row }) => {
        const invoice = row.original
        return (
          <div className="flex flex-col">
            <span className="text-sm">{invoice.customerName}</span>
            <span className="text-sm">{invoice.customerDocType} {formatDocNumber(invoice.customerDocNumber, invoice.customerDocType)}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'updatedAt',
      header: 'Fecha',
      cell: ({ row }) => {
        const invoice = row.original
        return (
          <div className="text-sm flex flex-col">
            <span>{invoice.updatedAt ? format(new Date(invoice.updatedAt), 'dd/MM/yyyy', { locale: es }) : '-'}</span>
            <span>{invoice.updatedAt ? format(new Date(invoice.updatedAt), 'HH:mm', { locale: es }) : '-'}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total',
      cell: ({ row }) => {
        const invoice = row.original
        return (
          <span className="font-medium">
            ${invoice.totalAmount.toLocaleString('es-AR')}
          </span>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ row }) => {
        const invoice = row.original
        return (
          <Badge className={`${getInvoiceStatusColor(invoice.status, invoice.voucherType)} w-[max-content]`}>
            {formatInvoiceStatus(invoice.status, invoice.voucherType)}
          </Badge>
        )
      },
    },
    {
      id: 'actions',
      header: 'Acciones',
      cell: ({ row }) => {
        const invoice = row.original
        const canGenerateCreditNote = invoice.status === InvoiceStatus.AUTHORIZED
          && invoice.voucherType === VoucherType.INVOICE
          && !invoice.relatedDocumentNumber
        const canGenerateInvoice = invoice.status !== InvoiceStatus.CANCELLED && (invoice.status !== InvoiceStatus.AUTHORIZED
          || invoice.voucherType === VoucherType.RECEIPT) && !invoice.relatedDocumentNumber
        return (
          <div className="flex gap-1">
            {invoice.fileId ? (
              <IconButton
                variant="primary"
                title='Descargar comprobante'
                onClick={() => handleOpenDocument(invoice.fileId!, invoice.fileName!)}
              >
                {getVoucherIcon(invoice.voucherType, invoice.invoiceNumber)}
              </IconButton>
            ) : (
              <IconButton
                variant="default"
                title="Documento no disponible"
                disabled
              >
                <Clipboard className="h-4 w-4 text-muted-foreground" />
              </IconButton>
            )}
            {canGenerateCreditNote ? (
              <IconButton
                variant="danger"
                onClick={() => navigate({ to: `${Paths.Management.InvoiceGenerationStatus}/${invoice.id}`, search: { voucherType: VoucherType.CREDIT_NOTE } })}
                title='Generar nota de crédito'
              >
                <CreditCard className="h-4 w-4" />
              </IconButton>
            ) : canGenerateInvoice ? (
              <IconButton
                variant="success"
                onClick={() => navigate({ to: `${Paths.Management.InvoiceGenerationStatus}/${invoice.id}`, search: { voucherType: VoucherType.INVOICE } })}
                title='Generar Factura'
              >
                <Receipt className="h-4 w-4" />
              </IconButton>
            ) : null}
            {invoice.voucherType === VoucherType.RECEIPT && (
              <IconButton
                variant="primary"
                onClick={() => navigate({ to: `${Paths.Management.InvoiceGenerationStatus}/${invoice.id}`, search: { voucherType: VoucherType.RECEIPT } })}
                title='Regenerar recibo'
              >
                <RefreshCw className="h-4 w-4" />
              </IconButton>
            )}
            {invoice.contractId && (
              <IconButton
                variant="primary"
                onClick={() => navigate({ to: `${Paths.Management.Contracts}/${invoice.contractId}` })}
                title='Ver contrato'
              >
                <ArrowRight className="h-4 w-4" />
              </IconButton>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <PageContainer>
      <NavHeader
        hideBackButton
        actions={
          <Button
            variant="outline"
            onClick={handleExportCsv}
            disabled={exporting}
          >
            <Download className="h-4 w-4 mr-1.5" />
            {exporting ? 'Exportando...' : 'Exportar CSV'}
          </Button>
        }
      >
        <h1 className="text-3xl font-bold tracking-tight">Facturación</h1>
        <p className="text-muted-foreground">
          Gestiona la facturación y emisión de comprobantes
        </p>
      </NavHeader>

      <Card>
        <CardContent className='pt-6'>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-center">
            <div className="space-y-2">
              <Label>Buscar</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar facturas..."
                  className="pl-8"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select value={statusFilter} onValueChange={(value) => {
                updateQueryParams({ status: value, page: 1 })
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos los estados" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value={InvoiceStatus.DRAFT}>Borrador</SelectItem>
                  <SelectItem value={InvoiceStatus.PENDING}>Pendiente</SelectItem>
                  <SelectItem value={InvoiceStatus.AUTHORIZED}>Autorizada</SelectItem>
                  <SelectItem value={InvoiceStatus.DELAYED}>Reintento</SelectItem>
                  <SelectItem value={InvoiceStatus.REJECTED}>Rechazada</SelectItem>
                  <SelectItem value={InvoiceStatus.CANCELLED}>Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo de Comprobante</Label>
              <Select value={voucherTypeFilter} onValueChange={(value) => {
                updateQueryParams({ voucherType: value, page: 1 })
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos los tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  <SelectItem value={VoucherType.INVOICE}>Factura</SelectItem>
                  <SelectItem value={VoucherType.CREDIT_NOTE}>Nota de Crédito</SelectItem>
                  {/* <SelectItem value={VoucherType.DEBIT_NOTE}>Nota de Débito</SelectItem> */}
                  <SelectItem value={VoucherType.RECEIPT}>Recibo</SelectItem>
                  {/* <SelectItem value={VoucherType.PROFORMA_INVOICE}>Proforma</SelectItem> */}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end mt-4">
            {/* <div className="grid grid-cols-2 gap-2"> */}
            <div className="space-y-2">
              <Label>Desde</Label>
              <DateSelectorComponent initialDate={fromDate ?? new Date()} onDateSelected={(date) => {
                setFromDate(date)
                updateQueryParams({ fromDate: format(date, 'yyyy-MM-dd') })
              }} />
            </div>
            <div className="space-y-2">
              <Label>Hasta</Label>
              <DateSelectorComponent initialDate={toDate ?? new Date()} onDateSelected={(date) => {
                setToDate(date)
                updateQueryParams({ toDate: format(date, 'yyyy-MM-dd') })
              }} />
            </div>
            {/* </div> */}
            {(search || statusFilter !== 'all' || voucherTypeFilter !== 'all' || fromDate || toDate) && (
              <div className='mb-1'>
                <IconButton variant="default" onClick={handleClearFilters}>
                  <X className="h-4 w-4" />
                </IconButton>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {invoices?.summary && (
        <Summary
          items={[
            { label: 'Total comprobantes', count: invoices.summary.totalCount, amount: invoices.summary.totalAmount },
            { label: 'Cancelados/Rechazados', count: invoices.summary.totalByStatus.cancelledOrRejected.count, amount: invoices.summary.totalByStatus.cancelledOrRejected.amount, variant: 'destructive' },
            { label: 'En Reintento', count: invoices.summary.totalByStatus.delayed.count, amount: invoices.summary.totalByStatus.delayed.amount, variant: 'warning' },
            { label: 'Autorizados - Facturas', count: invoices.summary.totalByStatus.authorized.count, amount: invoices.summary.totalByStatus.authorized.amount, variant: 'success' },
            { label: 'Autorizados - Notas de crédito', count: invoices.summary.totalByStatus.authorizedCreditNote.count, amount: invoices.summary.totalByStatus.authorizedCreditNote.amount, variant: 'destructive' },
          ]}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Listado de Facturas</CardTitle>
          <CardDescription>
            Visualización y gestión de todas las facturas emitidas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable<InvoiceDetail, unknown>
            columns={columns}
            data={invoices?.data || []}
            pageCount={invoices?.totalPages}
            pagination={pagination}
            onPaginationChange={(updater) => {
              const newPagination = typeof updater === 'function' ? updater(pagination) : updater
              setPagination(newPagination)
              updateQueryParams({ page: newPagination.pageIndex + 1, pageSize: newPagination.pageSize })
            }}
            totalRecords={invoices?.total}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
